/**
 * CPWD RA Bill workbook formulas (fixed statutory rounding on net excluding secured advance).
 * Inputs are non-negative commercial / withheld amounts; blanks treated as 0.
 */

export const nz = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
};

export const roundCpwd = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n);
};

/**
 * Manual commercial inputs (editable fields).
 */
export const extractCommercialInputs = (payload = {}) => ({
  gross_value_of_work_done: nz(
    payload.gross_value_of_work_done
      ?? payload.upto_date_gross_work_done
      ?? payload.gross_work_done,
  ),
  upto_date_secured_advance_on_material_brought_to_site: nz(
    payload.upto_date_secured_advance_on_material_brought_to_site
      ?? payload.sec_advance_material,
  ),
  mobilization_advance: nz(payload.mobilization_advance ?? payload.mobilisation_advance),
  clause_10cc_10ca_payment: nz(payload.clause_10cc_10ca_payment),
  advance_payment_work_done_but_not_measured: nz(
    payload.advance_payment_work_done_but_not_measured
      ?? payload.advance_payment_workdone_not_measured,
  ),
});

export const sumUptoDateGrossValue = (commercial) => (
  nz(commercial.gross_value_of_work_done)
  + nz(commercial.upto_date_secured_advance_on_material_brought_to_site)
  + nz(commercial.mobilization_advance)
  + nz(commercial.clause_10cc_10ca_payment)
  + nz(commercial.advance_payment_work_done_but_not_measured)
);

/**
 * Row-1 "gross value of work done" for CPWD Σ. If only the composite total was saved
 * (`upto_date_gross_value_of_work_done`) and work-only fields are blank/zero, infer work as
 * (total − secured advance − mob advance − 10CC/10CA − advance not measured) so recoveries match the workbook.
 */
export function resolveGrossValueOfWorkDoneForCpwd(bill, commercial = null) {
  const c = commercial ?? extractCommercialInputs(bill);
  const direct = nz(c.gross_value_of_work_done);
  if (direct > 0) return direct;
  const fromFlat = nz(
    bill?.upto_date_gross_work_done
      ?? bill?.gross_value_of_work_done
      ?? bill?.gross_work_done,
  );
  if (fromFlat > 0) return fromFlat;

  const cpwdTotal = nz(bill?.upto_date_gross_value_of_work_done);
  if (cpwdTotal <= 0) return 0;

  const other = nz(c.upto_date_secured_advance_on_material_brought_to_site)
    + nz(c.mobilization_advance)
    + nz(c.clause_10cc_10ca_payment)
    + nz(c.advance_payment_work_done_but_not_measured);

  return Math.max(0, cpwdTotal - other);
}

/**
 * @param {Object} opts
 * @param {number} opts.agreementAmount
 * @param {ReturnType<typeof extractCommercialInputs>} opts.commercialInputs
 * @param {number} opts.previousUptoDateGrossValue — previous bill's sumUptoDateGrossValue (0 if first)
 * @param {number} opts.previousUptoDateSecuredAdvance — previous bill's cumulative secured advance input (0 if first / unknown)
 * @param {boolean} opts.isFirstBill
 * @param {number} opts.addlSdForSpecialItems
 * @param {number} opts.totalWithheld — sum of withheld lines
 * @param {number} [opts.fallbackNetSincePreviousBill] CPWD row 5(a) from payload when Σ vs previous RA matches (net since prev → 0) but workbook lines are correct
 * @param {number} [opts.fallbackNetExclSecuredAdvance] CPWD row 5(b) — statutory recovery base when chain gives 0
 * @param {number} [opts.statutoryNetExclSecOverride] CPWD row 5(b) in Rs. — when > 0, always used as GST/IT/cess/SD base (meeting / bad Σ data)
 * @param {number} [opts.securityDepositOverride] When >= 0 (finite), replaces CPWD 2.5% SD; omit for automatic SD.
 */
export function computeCpwdRABillAmounts({
  agreementAmount,
  commercialInputs,
  previousUptoDateGrossValue = 0,
  previousUptoDateSecuredAdvance = 0,
  isFirstBill = false,
  addlSdForSpecialItems = 0,
  totalWithheld = 0,
  fallbackNetSincePreviousBill = 0,
  fallbackNetExclSecuredAdvance = 0,
  statutoryNetExclSecOverride = 0,
  securityDepositOverride,
  recoveryRateOverrides,
}) {
  const agreement = nz(agreementAmount);
  const commercial = commercialInputs || extractCommercialInputs({});
  const uptoDateGross = sumUptoDateGrossValue(commercial);
  const prevGross = isFirstBill ? 0 : nz(previousUptoDateGrossValue);
  const prevSec = isFirstBill ? 0 : nz(previousUptoDateSecuredAdvance);
  const secNow = nz(commercial.upto_date_secured_advance_on_material_brought_to_site);

  const grossValueAsPerPreviousRabill = prevGross;
  let netSincePrev = Math.max(0, uptoDateGross - grossValueAsPerPreviousRabill);
  const securedAdvanceAdded = isFirstBill
    ? secNow
    : Math.max(0, secNow - prevSec);
  let netExclSec = Math.max(0, netSincePrev - securedAdvanceAdded);

  const fs5a = nz(fallbackNetSincePreviousBill);
  const fs5b = nz(fallbackNetExclSecuredAdvance);

  if (netSincePrev === 0 && fs5a > 0) {
    netSincePrev = fs5a;
    netExclSec = Math.max(0, netSincePrev - securedAdvanceAdded);
  }
  if (netExclSec === 0 && fs5b > 0) {
    netExclSec = fs5b;
    if (netSincePrev === 0) {
      netSincePrev = fs5b + securedAdvanceAdded;
    }
  }

  const statOverride = nz(statutoryNetExclSecOverride);
  if (statOverride > 0) {
    netExclSec = statOverride;
    if (netSincePrev === 0) {
      netSincePrev = statOverride + securedAdvanceAdded;
    }
  }

  const sdOv = securityDepositOverride;
  const rateSd = nz(recoveryRateOverrides?.security_deposit_pct);
  const rateGst = nz(recoveryRateOverrides?.gst_pct);
  const rateIt = nz(recoveryRateOverrides?.income_tax_pct);
  const rateCess = nz(recoveryRateOverrides?.labour_cess_pct);
  const useRateOverrides = rateSd > 0 || rateGst > 0 || rateIt > 0 || rateCess > 0;

  const securityDeposit25 = Number.isFinite(Number(sdOv)) && Number(sdOv) >= 0
    ? roundCpwd(Number(sdOv))
    : roundCpwd(netExclSec * ((useRateOverrides ? rateSd : 2.5) / 100));
  const gst2 = roundCpwd(netExclSec * (100 / 118) * ((useRateOverrides ? rateGst : 2) / 100));
  const incomeTax2 = roundCpwd(
    netExclSec * (100 / 118) * (100 / 101) * (100 / 102) * ((useRateOverrides ? rateIt : 2) / 100),
  );
  const labourCess1 = roundCpwd(netExclSec * (100 / 118) * (100 / 101) * ((useRateOverrides ? rateCess : 1) / 100));
  const addlSd = nz(addlSdForSpecialItems);

  const totalRecoveries = securityDeposit25 + gst2 + incomeTax2 + labourCess1 + addlSd;
  const tw = nz(totalWithheld);
  const totalDeductions = totalRecoveries + tw;
  const netPaymentReceived = Math.max(0, Math.round(netSincePrev - totalDeductions));
  // Progress should track executed work against agreement value, excluding advance components.
  const progressRatio = agreement > 0
    ? nz(commercial.gross_value_of_work_done) / agreement
    : 0;

  return {
    upto_date_gross_value_of_work_done: Math.round(uptoDateGross),
    gross_value_as_per_previous_ra_bill: Math.round(grossValueAsPerPreviousRabill),
    net_value_of_work_done_since_previous_bill: Math.round(netSincePrev),
    secured_advance_added_in_this_bill: Math.round(securedAdvanceAdded),
    net_value_of_work_done_excluding_secured_advance: Math.round(netExclSec),
    security_deposit_2_5_percent: securityDeposit25,
    gst_2_percent: gst2,
    income_tax_2_percent: incomeTax2,
    labour_cess_1_percent: labourCess1,
    addl_sd_for_special_items: Math.round(addlSd),
    total_recoveries: Math.round(totalRecoveries),
    total_withheld_amounts: Math.round(tw),
    total_deductions: Math.round(totalDeductions),
    net_payment_received: netPaymentReceived,
    progress_percent: progressRatio,
    // legacy flat names used across the app
    upto_date_gross_inc_sec_adv: Math.round(uptoDateGross),
    previous_ra_gross_inc_sec_adv: Math.round(grossValueAsPerPreviousRabill),
  };
}

/** Maps CPWD compute output + agreement to fields used by RABillsRegister / API payloads. */
export function toLegacyFormulaShape(cpwd, { agreementAmount = 0 } = {}) {
  const agreement = nz(agreementAmount);
  return {
    ...cpwd,
    agreement_amount: Math.round(agreement),
    security_deposit: cpwd.security_deposit_2_5_percent,
    gst_deduction: cpwd.gst_2_percent,
    income_tax_deduction: cpwd.income_tax_2_percent,
    labour_cess: cpwd.labour_cess_1_percent,
    additional_sd_special_items: cpwd.addl_sd_for_special_items,
    net_value_work_done_since_prev_bill: cpwd.net_value_of_work_done_since_previous_bill,
    net_value_work_done_without_sec_adv: cpwd.net_value_of_work_done_excluding_secured_advance,
    current_bill_value: cpwd.net_value_of_work_done_since_previous_bill,
    gross_work_done: cpwd.net_value_of_work_done_since_previous_bill,
    total_withheld: cpwd.total_withheld_amounts,
    total_deductions: cpwd.total_deductions,
    total_recoveries: cpwd.total_recoveries,
    net_payment_received: cpwd.net_payment_received,
    percent_workdone: Number((cpwd.progress_percent * 100).toFixed(2)),
    previous_ra_upto_date_gross: cpwd.previous_ra_gross_inc_sec_adv,
    upto_date_gross_inc_sec_adv: cpwd.upto_date_gross_inc_sec_adv,
    bills_paid_by_dept: 0,
  };
}
